/**
 * Seed Remove Tag Modal Component
 *
 * Follows the same pattern as Technology and Asset sections for UI consistency.
 * Uses shared RemoveTag component for consistent UX across the platform.
 *
 * Key features:
 * - Supports bulk tag removal operations for multiple seeds
 * - Handles Asset, WebApplication, and ADObject seed types
 * - Fetches current seed data before update to prevent data loss
 * - Graceful handling of partial failures
 * - Accurate success/failure reporting with toast notifications
 */

import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { shouldUseBulk } from '@/hooks/useBulkAction/useBulkAction';
import { BulkSelectionState } from '@/hooks/useBulkSelection/types';
import { queryClient } from '@/queryclient';
import { RemoveTag } from '@/sections/RemoveTag';
import { extractSeedFromResponse } from '@/sections/seeds/utils/extractSeedFromResponse';
import { Seed } from '@/types';
import { useMutation } from '@/utils/api';
import { tagRemovedError, tagRemovedSuccess } from '@/utils/tagToastMessages';

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * Keys of seeds to remove tag from.
   *
   * INVARIANT: seedKeys MUST be derived from the SAME source as selectionState.
   * Callers either:
   *   (a) pass neither selectionState nor onBulkRequested (per-item modal), OR
   *   (b) pass selectionState; seedKeys should be selectionState.keys or equivalent.
   * NEVER pass selectionState with N=1000 keys alongside seedKeys=[single].
   */
  seedKeys: string[];
  onSuccess?: () => void;
  initialTag?: string;
  /** Bulk selection state from caller. When provided AND shouldUseBulk returns true,
   * the modal delegates to onBulkRequested instead of per-item PUTs. */
  selectionState?: BulkSelectionState;
  /** Called when bulk path is triggered; caller handles BulkConfirmationDialog. */
  onBulkRequested?: (tag: string) => void;
}

/**
 * Modal component for removing tags from seeds
 *
 * Uses shared RemoveTag component for consistent UX across the platform.
 * Handles both single seed (from drawer) and bulk operations (from table).
 *
 * Features:
 * - Auto-fetches common tags across selected seeds
 * - Visual tag selection interface
 * - Graceful handling of partial failures
 * - Accurate success/failure reporting
 *
 * Seeds can be different types (Asset, WebApplication, ADObject), so we
 * need to determine the correct endpoint based on the seed's class field.
 *
 * Example usage:
 * ```tsx
 * <SeedRemoveTag
 *   open={modalState.removeTagModal}
 *   onClose={modalHandlers.closeRemoveTagModal}
 *   seedKeys={selectedKeys}
 *   onSuccess={clearSelection}
 * />
 * ```
 *
 * @param props - Component props
 * @returns Modal component
 */
export const SeedRemoveTag = ({
  open,
  onClose,
  seedKeys,
  onSuccess,
  initialTag,
  selectionState,
  onBulkRequested,
}: Props) => {
  const axios = useAxios();

  const { mutateAsync: removeTag, status: removingTag } = useMutation({
    defaultErrorMessage: 'Failed to remove tag',
    mutationFn: async (tagToRemove: string) => {
      const promises = seedKeys.map(async seedKey => {
        // Fetch current seed to get both tags AND status
        const currentSeed = await axios.get(`/my`, {
          params: {
            key: seedKey,
            exact: true,
          },
        });

        const seed = extractSeedFromResponse(currentSeed.data);
        if (!seed) {
          throw new Error(`Seed not found: ${seedKey}`);
        }
        const typedSeed = seed as Seed;
        const existingTags = typedSeed?.tags || [];
        const currentStatus = typedSeed?.status || 'A';

        // Remove the tag
        const updatedTags = existingTags.filter(
          (tag: string) => tag !== tagToRemove
        );

        const payload = {
          key: seedKey,
          status: currentStatus, // Include current status (required by backend)
          tags: updatedTags,
        };

        return axios.put('/seed', payload);
      });

      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled(promises);

      // Count successes and failures
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      // Invalidate cache if any operations succeeded
      if (succeeded > 0) {
        // Use predicate-based invalidation matching Technology pattern
        await queryClient.invalidateQueries({
          predicate: query => {
            const queryKey = query.queryKey as string[];
            return queryKey[0] === 'MY' && queryKey[1] === 'seed';
          },
          refetchType: 'all',
        });
      }

      // Log failures for debugging with enhanced error details
      if (failed > 0) {
        const failedResults = results
          .map((r, i) => ({ result: r, index: i }))
          .filter(
            (item): item is { result: PromiseRejectedResult; index: number } =>
              item.result.status === 'rejected'
          )
          .map(({ result, index }) => ({
            seedKey: seedKeys[index],
            error: result.reason,
            errorResponse: result.reason?.response,
            errorData: result.reason?.response?.data,
          }));
        console.error(
          '[SeedRemoveTag] Failed to remove tag from seeds:',
          failedResults
        );
      }

      // Return result summary for handleSubmit to use
      return { succeeded, failed, tagToRemove };
    },
  });

  const handleSubmit = async (tag: string) => {
    // Dev-mode divergence guard (R4 regression prevention)
    if (
      import.meta.env.DEV &&
      selectionState &&
      selectionState.mode === 'manual' &&
      seedKeys.length > 0 &&
      selectionState.keys.length !== seedKeys.length
    ) {
      console.warn(
        '[SeedRemoveTag] selectionState.keys.length diverges from seedKeys.length — ' +
          'likely a caller bug. Bulk path will operate on selectionState.'
      );
    }

    // Bulk path: delegate to caller's BulkConfirmationDialog
    if (selectionState && shouldUseBulk(selectionState) && onBulkRequested) {
      onBulkRequested(tag);
      onClose();
      return;
    }

    try {
      const result = await removeTag(tag);

      // Show accurate feedback based on results
      if (result.succeeded > 0 && result.failed === 0) {
        const count = result.succeeded > 1 ? result.succeeded : undefined;
        toast.success(tagRemovedSuccess('seed', count));
      } else if (result.succeeded > 0 && result.failed > 0) {
        toast.warning(
          `Tag "${result.tagToRemove}" removed from ${result.succeeded} ${result.succeeded > 1 ? 'seeds' : 'seed'}, ` +
            `but failed for ${result.failed} ${result.failed > 1 ? 'seeds' : 'seed'}`
        );
      } else {
        toast.error(
          `Failed to remove tag "${result.tagToRemove}" from ${result.failed} ${result.failed > 1 ? 'seeds' : 'seed'}`
        );
      }

      onClose();
      onSuccess?.();
    } catch (error: unknown) {
      console.error('[SeedRemoveTag] Tag removal failed:', error);
      const message = error instanceof Error ? error.message : 'Unknown error';
      toast.error(tagRemovedError('seed', message));
    }
  };

  return (
    <RemoveTag
      open={open}
      onClose={onClose}
      disabled={removingTag === 'pending'}
      onSubmit={handleSubmit}
      assetKeys={seedKeys}
      label="seed"
      initialTag={initialTag}
    />
  );
};
